import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`You can connect your BigQuery tables to PostHog by configuring it as a source. You must grant a limited set of permissions so the connector can create, query, and delete temporary tables without exposing your entire BigQuery environment.`}</p>
    <h2 {...{
      "id": "requirements"
    }}>{`Requirements`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://cloud.google.com/iam/docs/service-account-overview"
        }}>{`A Google Cloud Service Account`}</a>{` with the permissions described below`}</li>
      <li parentName="ul">{`Google Cloud JSON Key file for that account's Dataset ID`}</li>
      <li parentName="ul">{`(Optional) A Dataset ID for temporary tables`}</li>
    </ul>
    <h2 {...{
      "id": "configuring-bigquery"
    }}>{`Configuring BigQuery`}</h2>
    <p>{`To securely connect your BigQuery account to PostHog, create a dedicated service account with the minimum required permissions:`}</p>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Create a service account:`}</strong></li>
    </ol>
    <ul>
      <li parentName="ul">{`Go to the `}<a parentName="li" {...{
          "href": "https://console.cloud.google.com/"
        }}><strong parentName="a">{`Google Cloud Console.`}</strong></a></li>
      <li parentName="ul">{`Navigate to `}<strong parentName="li">{`IAM & Admin > Service Accounts.`}</strong></li>
      <li parentName="ul">{`Click `}<strong parentName="li">{`Create Service Account.`}</strong></li>
      <li parentName="ul">{`Provide a descriptive name (e.g., `}<inlineCode parentName="li">{`bigquery-posthog-service-account`}</inlineCode>{`) and a brief description.`}</li>
    </ul>
    <ol {...{
      "start": 2
    }}>
      <li parentName="ol"><strong parentName="li">{`Assign required permissions:`}</strong></li>
    </ol>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`For simplicity, you can assign the `}<strong parentName="p">{`BigQuery Data Editor`}</strong>{`, `}<strong parentName="p">{`BigQuery Job User`}</strong>{`, and `}<strong parentName="p">{`BigQuery Read Session User`}</strong>{` roles if it meets your security requirements.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Alternatively, create a custom role that includes only these permissions:`}</p>
        <pre parentName="li"><code parentName="pre" {...{}}>{`bigquery.readsessions.create
bigquery.readsessions.getData
bigquery.datasets.get
bigquery.jobs.create
bigquery.tables.get
bigquery.tables.list
bigquery.tables.getData
bigquery.tables.create
bigquery.tables.updateData
bigquery.tables.delete
`}</code></pre>
      </li>
    </ul>
    <ol {...{
      "start": 3
    }}>
      <li parentName="ol"><strong parentName="li">{`Generate and download the service account key:`}</strong></li>
    </ol>
    <ul>
      <li parentName="ul">{`Once the service account is created, click on it and select the `}<strong parentName="li">{`Keys`}</strong>{` tab.`}</li>
      <li parentName="ul">{`Click `}<strong parentName="li">{`Add Key > Create new key`}</strong>{`, choose `}<strong parentName="li">{`JSON`}</strong>{`, and download the key file.`}</li>
      <li parentName="ul"><strong parentName="li">{`Important:`}</strong>{` Store the JSON key securely, as it contains sensitive credentials.`}</li>
    </ul>
    <h2 {...{
      "id": "configuring-posthog"
    }}>{`Configuring PostHog`}</h2>
    <ol>
      <li parentName="ol">{`In PostHog, go to the `}<strong parentName="li"><a parentName="strong" {...{
            "href": "https://us.posthog.com/pipeline/sources"
          }}>{`Data pipelines`}</a></strong>{` tab, then choose `}<strong parentName="li">{`sources`}</strong></li>
      <li parentName="ol">{`Click `}<strong parentName="li">{`New source`}</strong>{` and select BigQuery`}</li>
      <li parentName="ol">{`Drag and drop the `}<strong parentName="li">{`Google Cloud JSON Key file`}</strong>{` to upload`}</li>
      <li parentName="ol">{`Enter the `}<strong parentName="li">{`Dataset ID`}</strong>{` you want to import`}</li>
      <li parentName="ol">{`(Optional) If you're limiting permissions to the service account provided, enter a Dataset ID for temporary tables`}</li>
      <li parentName="ol">{`(Optional) Add a prefix for the table name`}</li>
    </ol>
    <blockquote>
      <p parentName="blockquote">{`In some rare instances BigQuery is unable to auto-locate your dataset unless you specify a region. If you see an error message that looks something like:`}</p>
      <p parentName="blockquote"><inlineCode parentName="p">{`Dataset xxx:xxx was not found in region`}</inlineCode></p>
      <p parentName="blockquote">{`then you may need to toggle the switch for manually specifying your region. Regions typically look like `}<inlineCode parentName="p">{`us-east1`}</inlineCode>{` or similar.`}</p>
    </blockquote>
    <h2 {...{
      "id": "how-it-works"
    }}>{`How it works`}</h2>
    <p>{`PostHog creates and deletes `}<a parentName="p" {...{
        "href": "https://cloud.google.com/bigquery/docs/writing-results#temporary_and_permanent_tables"
      }}>{`temporary tables`}</a>{` when querying your data. This is necessary for handling large BigQuery tables. Temporary tables help break down large data processing tasks into manageable chunks. However, they incur storage and query costs in BigQuery while they exist. We delete them as soon as the job is done.`}</p>
    <p>{`PostHog requires a unique primary key when importing data and will look for it in this order:`}</p>
    <ol>
      <li parentName="ol">{`PostHog will use the `}<inlineCode parentName="li">{`id`}</inlineCode>{` column as the primary key if it exists`}</li>
      <li parentName="ol">{`If the `}<inlineCode parentName="li">{`id`}</inlineCode>{` column does not exist, PostHog will use any primary key constraints in the table`}</li>
    </ol>
    <p>{`After selecting the primary key, PostHog will query the table to see if the column is unique. If it is not, PostHog will fail the import with a `}<inlineCode parentName="p">{`DuplicatePrimaryKeysException`}</inlineCode>{`. If you have no `}<inlineCode parentName="p">{`id`}</inlineCode>{` column and no primary key constraints, future incremental imports will fail.`}</p>
    <h3 {...{
      "id": "costs"
    }}>{`Costs`}</h3>
    <p>{`We minimize BigQuery costs by keeping queries to a minimum and deleting temporary tables immediately after use. Although the connector automates temporary table management, check `}<a parentName="p" {...{
        "href": "https://cloud.google.com/bigquery/pricing"
      }}>{`BigQuery’s pricing`}</a>{` for details on storage and query costs.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      